// opervex-frontend/src/utils/SelectModal.js

import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { Modal, Button, Table, Form } from "react-bootstrap";

export const SelectModal = ({ title, fields, items, multiple = true, maxSelection = null }) => {
  return new Promise((resolve) => {
    const div = document.createElement("div");
    document.body.appendChild(div);

    const root = ReactDOM.createRoot(div);

    const SelectModalWrapper = () => {
      const [show, setShow] = useState(true);
      const [selectedIndexes, setSelectedIndexes] = useState(() => {
        // Inicializa com os índices dos itens que têm selected=true
        return items.reduce((acc, item, index) => {
          if (item.selected) acc.push(index);
          return acc;
        }, []);
      });

      const [selectionLimitReached, setSelectionLimitReached] = useState(false);

      useEffect(() => {
        if (maxSelection && selectedIndexes.length >= maxSelection) {
          setSelectionLimitReached(true);
        } else {
          setSelectionLimitReached(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [selectedIndexes, maxSelection]);

      const handleClose = () => {
        setShow(false);
        setTimeout(() => {
          root.unmount();
          div.remove();
          resolve(null);
        }, 300);
      };

      const toggleItemSelection = (index) => {
        if (multiple) {
          if (selectedIndexes.includes(index)) {
            setSelectedIndexes((prev) => prev.filter((i) => i !== index));
          } else {
            if (maxSelection && selectedIndexes.length >= maxSelection) {
              return;
            }
            setSelectedIndexes((prev) => [...prev, index]);
          }
        } else {
          setSelectedIndexes([index]);
        }
      };

      const handleSelectAll = (e) => {
        if (e.target.checked) {
          if (maxSelection) {
            setSelectedIndexes(Array.from({ length: Math.min(items.length, maxSelection) }, (_, i) => i));
          } else {
            setSelectedIndexes(Array.from({ length: items.length }, (_, i) => i));
          }
        } else {
          setSelectedIndexes([]);
        }
      };

      const handleCancel = () => {
        handleClose();
      };

      const handleConfirm = () => {
        const selectedItems = selectedIndexes.map((index) => items[index]);
        setShow(false);
        setTimeout(() => {
          root.unmount();
          div.remove();
          resolve(multiple ? selectedItems : selectedItems[0]);
        }, 300);
      };

      return (
        <Modal show={show} onHide={handleClose} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Table striped bordered hover size="sm" responsive>
              <thead>
                <tr className="text-center align-middle">
                  {multiple && (
                    <th className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={
                          selectedIndexes.length > 0 &&
                          selectedIndexes.length === Math.min(items.length, maxSelection || items.length)
                        }
                        onChange={handleSelectAll}
                        disabled={maxSelection ? items.length > maxSelection : false}
                      />
                    </th>
                  )}
                  {fields?.map((field) => (
                    <th key={field.key} className="text-center">
                      {field.description ? field.description : field.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items?.map((item, index) => {
                  const isSelected = selectedIndexes.includes(index);
                  const isDisabled = !isSelected && selectionLimitReached;

                  return (
                    <tr
                      key={index}
                      onClick={() => !isDisabled && toggleItemSelection(index)}
                      className={isSelected ? "table-primary" : ""}
                      style={{
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        opacity: isDisabled ? 0.6 : 1,
                      }}
                    >
                      {multiple && (
                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          <Form.Check
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => !isDisabled && toggleItemSelection(index)}
                            disabled={isDisabled}
                          />
                        </td>
                      )}
                      {fields?.map((field) => (
                        <td key={`${index}-${field.key}`} className="text-center">
                          {item[field.key]}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <div className="me-auto">
              {maxSelection && (
                <span className="text-muted">
                  Selecionados: {selectedIndexes.length}/{maxSelection}
                </span>
              )}
            </div>
            <Button variant="secondary" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleConfirm} disabled={selectedIndexes.length === 0}>
              Confirmar
            </Button>
          </Modal.Footer>
        </Modal>
      );
    };

    root.render(<SelectModalWrapper />);
  });
};
